/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFullWidthLayoutWidth } from '@atlaskit/editor-shared-styles';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { sectionCtaMessages } from '@atlassian/editor-plugin-company-hub-common/messages';

import { type AnchorRectCache, isAnchorSupported } from '../anchor-utils';

const styles = cssMap({
	containerBaseStyles: {
		position: 'absolute',
		// @ts-expect-error - we need this company hub specific variable to match the editor styles
		width: 'calc(100cqw - var(--company-hub-editor-gutter-padding-inline) * 2 - 8px)',
		// @ts-expect-error - we need this company hub specific variable to match the editor styles
		paddingInline: 'var(--company-hub-editor-gutter-padding-inline)',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '32px',
		opacity: 0,
		transition: 'opacity 0.2s',
		zIndex: 100,

		'&:hover': {
			opacity: 1,
		},
	},
	lineStyles: {
		flex: 1,
		height: '2px',
		backgroundColor: token('color.background.neutral.bold'),
	},
	pressable: {
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.full'),
		height: '32px',
		width: '32px',
		'&:hover': {
			backgroundColor: token('color.background.neutral.bold.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.bold.pressed'),
		},
	},
});

const Line = () => <Box xcss={cx(styles.lineStyles)} />;

interface PlusButtonProps {
	anchorId: string;
	anchorRectCache?: AnchorRectCache;
	isLastPosition?: boolean;
	onClick: (event: React.MouseEvent) => void;
	prevNodeTypeName?: string;
	view: EditorView;
}

export const PlusButton = ({
	anchorId,
	onClick,
	view,
	prevNodeTypeName,
	isLastPosition,
	anchorRectCache,
}: PlusButtonProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [dynamicStyles, setDynamicStyles] = useState({});

	useEffect(() => {
		const calculatePosition = () => {
			const isSectionNode = prevNodeTypeName === 'multiBodiedExtension';
			const isParagraphNode = prevNodeTypeName === 'paragraph';
			const isTableNode = prevNodeTypeName === 'table';

			let verticalOffset = '-50%'; // Default for top and after normal nodes
			if (isSectionNode || isTableNode) {
				verticalOffset = '-100%';
			} else if (isLastPosition && isParagraphNode) {
				verticalOffset = '0';
			}

			const baseStyles = {
				// Linter errors if we use variables/non-tokens in cssMap so we need to use style prop
				// add 40px to the max width to account for the padding of the editor
				maxWidth: `${akEditorFullWidthLayoutWidth + 40}px`,
			};

			if (isAnchorSupported()) {
				setDynamicStyles({
					...baseStyles,
					positionAnchor: `--${anchorId}`,
					top: 'anchor(bottom)',
					left: '50%',
					transform: `translate(-50%, ${verticalOffset})`,
				});
				return;
			}

			// Fallback logic
			anchorRectCache?.clear();
			const rect = anchorRectCache?.getRect(anchorId);
			if (!rect) {
				setDynamicStyles({ ...baseStyles, display: 'none' });
				return;
			}

			const top = rect.top + rect.height;

			setDynamicStyles({
				...baseStyles,
				top: `${top}px`,
				left: '50%',
				transform: `translate(-50%, ${verticalOffset})`,
			});
		};

		// Set position on initial render and whenever dependencies change.
		calculatePosition();

		// Observe for layout changes to recalculate position.
		const observer = new ResizeObserver(calculatePosition);
		observer.observe(view.dom);

		return () => {
			observer.disconnect();
		};
	}, [anchorId, prevNodeTypeName, isLastPosition, anchorRectCache, view.dom]);

	return (
		<div
			css={styles.containerBaseStyles}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={dynamicStyles}
			data-testid="company-hub-plus-button"
		>
			<Line />
			<Tooltip content={formatMessage(sectionCtaMessages.addSection)}>
				{(tooltipProps) => (
					<Pressable
						{...tooltipProps}
						onClick={onClick}
						xcss={styles.pressable}
						aria-label={formatMessage(sectionCtaMessages.addSection)}
					>
						<AddIcon label="" color={token('color.icon.inverse')} />
					</Pressable>
				)}
			</Tooltip>
			<Line />
		</div>
	);
};
